/**
 * KpiSummaryRow — row of KPI cards. Primary KPI at 1.5x width (col-span-3 in 6-col grid).
 * Each card: KPI label, value, delta via ComparisonBadge, percentile rank.
 * Respects higherIsBetter for delta coloring.
 */

import { ComparisonBadge } from '@/components/dashboard/shared/ComparisonBadge';
import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { useVerdictSummary } from '@/hooks/useDashboard';
import { fmtKpiValue as fmtKpi } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { KpiSummaryCard } from '@/types/dashboard';

// ============================================================================
// HELPERS
// ============================================================================

function formatKpiValue(value: number, unit: string): string {
  return fmtKpi(value, unit);
}

// ============================================================================
// SINGLE CARD
// ============================================================================

function sourceBadgeText(source: 'auto-goal' | 'auto-fallback' | 'manual' | undefined): string | null {
  if (source === 'auto-goal') return '(auto)';
  if (source === 'auto-fallback') return '(fallback)';
  return null; // 'manual' or undefined: no badge
}

function KpiCard({ kpi, source }: { kpi: KpiSummaryCard; source?: 'auto-goal' | 'auto-fallback' | 'manual' }) {
  const isTop30 = kpi.percentile >= 70;

  return (
    <div
      className={cn(
        'rounded-xl p-3 bg-surface-subtle',
        kpi.isPrimary && 'col-span-3'
      )}
    >
      <div className="text-xs text-secondary">
        {kpi.label}
        {kpi.isPrimary && sourceBadgeText(source) && (
          <span className="text-xs text-muted ml-1" data-testid="kpi-source-badge">
            {sourceBadgeText(source)}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span
          className={cn(
            'font-bold text-primary',
            kpi.isPrimary ? 'text-3xl' : 'text-xl'
          )}
        >
          {formatKpiValue(kpi.value, kpi.unit)}
        </span>
        <ComparisonBadge
          delta={kpi.delta}
          unit={kpi.unit}
          higherIsBetter={kpi.higherIsBetter}
        />
      </div>
      {isTop30 && (
        <div className="mt-1">
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-success/10 text-success font-medium">
            Top {100 - kpi.percentile}%
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function KpiSummaryRow() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  const kpis = data?.kpiSummary;
  if (!kpis || kpis.length === 0) {
    return (
      <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-4">
        <div className="text-sm font-semibold text-primary mb-2">Performance KPIs</div>
        <div className="text-xs text-secondary py-3 text-center">
          Configure KPIs in template settings to see KPI summary.
        </div>
      </div>
    );
  }

  // Sort: primary first
  const sorted = [...kpis].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-primary">Performance KPIs</div>
        <CsvExport
          headers={['KPI', 'Value', 'Unit', 'Delta', 'Percentile', 'Primary']}
          rows={sorted.map((kpi) => [
            kpi.label,
            kpi.value,
            kpi.unit,
            kpi.delta,
            kpi.percentile,
            kpi.isPrimary ? 'Yes' : 'No',
          ])}
          filename="kpi-summary"
        />
      </div>
      <div className="grid grid-cols-6 gap-3">
        {sorted.map((kpi) => (
          <KpiCard key={kpi.kpiId} kpi={kpi} source={data?.resolvedKpiConfig?.source} />
        ))}
      </div>
    </div>
  );
}
